import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useApp } from '@/store/useApp';

interface Props {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
}

interface Pos {
  left: number;
  top: number;
}

const POPOVER_W = 288;
const POPOVER_H = 180;

export function CommentPopover({ iframeRef }: Props) {
  const pending = useApp((s) => s.pendingComment);
  const setPending = useApp((s) => s.setPendingComment);
  const saveComment = useApp((s) => s.saveComment);
  const ref = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState('');
  const [pos, setPos] = useState<Pos | null>(null);

  useEffect(() => {
    setText('');
  }, [pending?.selector]);

  // Anchor against the iframe's host-viewport rect so positions account for canvas
  // scrolling, device-frame resizing, and panel layout — the previous version anchored
  // against the scrollable wrapper which made popovers drift on long pages.
  useLayoutEffect(() => {
    if (!pending) {
      setPos(null);
      return;
    }
    function place() {
      const iframeRect = iframeRef.current?.getBoundingClientRect();
      if (!iframeRect) return;
      const rawLeft = iframeRect.left + pending!.rect.x;
      const rawTop = iframeRect.top + pending!.rect.y + pending!.rect.height + 8;
      const left = Math.max(8, Math.min(rawLeft, window.innerWidth - POPOVER_W - 8));
      const top = Math.max(
        8,
        Math.min(rawTop, window.innerHeight - POPOVER_H - 8),
      );
      setPos({ left, top });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [pending, iframeRef]);

  if (!pending || !pos) return null;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Add comment"
      style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 60, width: POPOVER_W }}
      className="rounded-md border border-neutral-200 bg-white p-3 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
    >
      <p className="mb-2 truncate text-[11px] text-neutral-400" title={pending.selector}>
        {pending.selector}
      </p>
      <textarea
        autoFocus
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What should change about this element?"
        rows={3}
        className="mb-2 w-full resize-none rounded border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-950"
      />
      <p className="mb-2 text-[11px] text-neutral-500 dark:text-neutral-400">
        Adds this targeted change to the chat composer. Drop more comments, then send all at
        once from the chat.
      </p>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setPending(null)}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!text.trim()}
          onClick={() => {
            void saveComment(text);
          }}
        >
          Add to message
        </Button>
      </div>
    </div>
  );
}
